import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOrg, useCreateOrg, useUpdateOrg, useInviteMember, useJoinOrg, useCancelInvite, useRemoveMember, useSetMemberRole } from "@/hooks/useOrg";
import { KnowledgeBase } from "@/components/KnowledgeBase";
import { TeamsManager } from "@/components/TeamsManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Users, UserPlus, Trash2, Copy, Crown, LogIn, Palette, Check, ShieldCheck, ShieldOff } from "lucide-react";

export default function Organization() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const { data, isLoading } = useOrg();

  const org = data?.org;
  const role = data?.role;
  const isOwner = role === "owner";
  const isManager = role === "owner" || role === "manager";

  const afterMembership = async () => { await refresh(); };

  const createOrg = useCreateOrg({ onSuccess: async () => { toast.success("Organization created!"); await afterMembership(); }, onError: (e) => toast.error(apiError(e)) });
  const joinOrg = useJoinOrg({ onSuccess: async () => { toast.success("You've joined the organization!"); await afterMembership(); }, onError: (e) => toast.error(apiError(e)) });
  const updateOrg = useUpdateOrg({ onSuccess: () => toast.success("Organization updated"), onError: (e) => toast.error(apiError(e)) });
  const invite = useInviteMember({ onSuccess: (inv) => toast.success(`Invite created — code ${inv.code}`), onError: (e) => toast.error(apiError(e)) });
  const cancelInvite = useCancelInvite({ onError: (e) => toast.error(apiError(e)) });
  const removeMember = useRemoveMember({ onSuccess: () => toast.success("Member removed"), onError: (e) => toast.error(apiError(e)) });
  const setRole = useSetMemberRole({ onSuccess: (d) => toast.success(`Role updated to ${d.role}`), onError: (e) => toast.error(apiError(e)) });

  const [name, setName] = useState("");
  const [code, setCode] = useState(params.get("join") || "");
  const [inviteEmail, setInviteEmail] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [brandName, setBrandName] = useState("");

  const copyCode = (c) => { navigator.clipboard?.writeText(c); toast.success("Copied invite code"); };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="h-5 w-5 text-primary" />
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">Organization</p>
        </div>

        {isLoading ? (
          <div className="py-20 flex justify-center"><div className="h-8 w-8 border-2 border-primary border-t-transparent animate-spin rounded-full" /></div>
        ) : !org ? (
          /* No org: create or join */
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div className="border border-border bg-card p-8" data-testid="create-org-card">
              <Crown className="h-6 w-6 text-primary mb-4" />
              <h2 className="font-display text-2xl font-bold mb-2">Start an organization</h2>
              <p className="text-sm text-muted-foreground mb-6">Create a team workspace and invite your employees to learn — everyone gets unlimited courses.</p>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Organization name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corp" className="rounded-none h-11 mb-4" data-testid="org-name-input" />
              <Button onClick={() => name.trim() && createOrg.mutate(name.trim())} disabled={createOrg.isPending} className="rounded-none h-11 bg-primary hover:opacity-90 w-full" data-testid="create-org-button">
                {createOrg.isPending ? "Creating..." : "Create organization"}
              </Button>
            </div>
            <div className="border border-border bg-card p-8" data-testid="join-org-card">
              <LogIn className="h-6 w-6 text-primary mb-4" />
              <h2 className="font-display text-2xl font-bold mb-2">Join with a code</h2>
              <p className="text-sm text-muted-foreground mb-6">Got an invite code from your manager? Enter it to join your team.</p>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Invite code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. 0731D22D" className="rounded-none h-11 mb-4 uppercase" data-testid="join-code-input" />
              <Button onClick={() => code.trim() && joinOrg.mutate(code.trim())} disabled={joinOrg.isPending} variant="outline" className="rounded-none h-11 w-full" data-testid="join-org-button">
                {joinOrg.isPending ? "Joining..." : "Join organization"}
              </Button>
            </div>
          </div>
        ) : (
          /* Has org */
          <>
            <div className="flex items-center gap-4 mt-3 mb-8">
              {org.logo_url ? (
                <img src={org.logo_url} alt="logo" className="h-12 w-12 object-contain border border-border bg-card" />
              ) : (
                <div className="h-12 w-12 bg-primary text-primary-foreground flex items-center justify-center font-display font-extrabold text-xl">{org.name?.[0]}</div>
              )}
              <div>
                <h1 className="font-display text-4xl font-extrabold tracking-tighter" data-testid="org-name-heading">{org.name}</h1>
                <p className="text-muted-foreground text-sm">{isOwner ? "You manage this organization" : "You're a member of this organization"}</p>
              </div>
            </div>

            {isOwner && (
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Invite */}
                <div className="border border-border bg-card p-6" data-testid="invite-card">
                  <div className="flex items-center gap-2 mb-4"><UserPlus className="h-5 w-5 text-primary" /><h3 className="font-display font-bold">Invite an employee</h3></div>
                  <div className="flex gap-2">
                    <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="employee@company.com" className="rounded-none h-11" data-testid="invite-email-input" />
                    <Button onClick={() => { if (inviteEmail.trim()) { invite.mutate(inviteEmail.trim()); setInviteEmail(""); } }} disabled={invite.isPending} className="rounded-none h-11 bg-primary hover:opacity-90" data-testid="invite-button">Invite</Button>
                  </div>
                  {data.invites?.length > 0 && (
                    <div className="mt-4 space-y-2" data-testid="pending-invites">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">Pending invites</p>
                      {data.invites.map((inv) => (
                        <div key={inv.id} className="flex items-center justify-between border border-border p-2 text-sm">
                          <span className="truncate">{inv.email}</span>
                          <div className="flex items-center gap-2">
                            <button onClick={() => copyCode(inv.code)} className="font-mono text-primary inline-flex items-center gap-1" data-testid={`copy-code-${inv.id}`}>{inv.code} <Copy className="h-3 w-3" /></button>
                            <button onClick={() => cancelInvite.mutate(inv.id)} className="text-muted-foreground hover:text-destructive" data-testid={`cancel-invite-${inv.id}`}><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* White-label */}
                <div className="border border-border bg-card p-6" data-testid="branding-card">
                  <div className="flex items-center gap-2 mb-4"><Palette className="h-5 w-5 text-primary" /><h3 className="font-display font-bold">White-label branding</h3></div>
                  <Label className="text-xs uppercase tracking-wider mb-2 block">Organization name</Label>
                  <Input defaultValue={org.name} onChange={(e) => setBrandName(e.target.value)} className="rounded-none h-11 mb-3" data-testid="brand-name-input" />
                  <Label className="text-xs uppercase tracking-wider mb-2 block">Logo URL</Label>
                  <Input defaultValue={org.logo_url || ""} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://..." className="rounded-none h-11 mb-4" data-testid="logo-url-input" />
                  <Button onClick={() => updateOrg.mutate({ name: brandName || org.name, logo_url: logoUrl })} disabled={updateOrg.isPending} variant="outline" className="rounded-none h-11 w-full gap-1" data-testid="save-branding-button">
                    <Check className="h-4 w-4" /> Save branding
                  </Button>
                </div>
              </div>
            )}

            {isManager && <KnowledgeBase />}
            {isManager && <TeamsManager members={data.members || []} />}

            {/* Members */}
            <div className="border border-border bg-card">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-border"><Users className="h-5 w-5 text-primary" /><h3 className="font-display font-bold">Team members ({data.members?.length || 0})</h3></div>
              <div className="divide-y divide-border" data-testid="members-list">
                {(data.members || []).map((m) => (
                  <motion.div key={m.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-between px-5 py-3" data-testid={`member-${m.id}`}>
                    <div>
                      <div className="font-semibold text-sm flex items-center gap-2">
                        {m.name}
                        {m.org_role === "owner" && <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 uppercase tracking-wide">Owner</span>}
                        {m.org_role === "manager" && <span className="text-[10px] bg-foreground text-background px-1.5 py-0.5 uppercase tracking-wide" data-testid={`manager-badge-${m.id}`}>Manager</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">{m.email}</div>
                    </div>
                    {isOwner && m.org_role !== "owner" && (
                      <div className="flex items-center gap-3">
                        {m.org_role === "manager" ? (
                          <button onClick={() => setRole.mutate({ id: m.id, role: "member" })} title="Revoke manager" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs" data-testid={`demote-member-${m.id}`}><ShieldOff className="h-4 w-4" /> Revoke</button>
                        ) : (
                          <button onClick={() => setRole.mutate({ id: m.id, role: "manager" })} title="Make manager" className="text-muted-foreground hover:text-primary inline-flex items-center gap-1 text-xs" data-testid={`promote-member-${m.id}`}><ShieldCheck className="h-4 w-4" /> Make manager</button>
                        )}
                        <button onClick={() => removeMember.mutate(m.id)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-member-${m.id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
